import { createRoot } from 'react-dom/client';
import Lib from './app/lib';
import { StrictMode } from 'react';
import {
  purchaseDetector,
  initShadowRoot,
  cleanupShadowRoot,
} from '@superbar-ui';

const cdnUrl =
  import.meta.env.VITE_SUPERBAR_CDN_URL || 'https://cdn.trycaddie.com';

const customStylesBucketUrl = import.meta.env.VITE_CUSTOM_STYLES_BUCKET_URL;

const caddieScript = document.querySelector<HTMLScriptElement>(
  'script[superbar-data-store-id]'
);
const storeId = caddieScript?.getAttribute('superbar-data-store-id');

// store the store id on the window for use later
if (storeId) {
  (window as Window & { superbarConfig?: { storeId: string } }).superbarConfig =
    { storeId };
}

// Inject custom store stylesheet into the main document early so merchant-page
// styles load in parallel with React initialization.
if (customStylesBucketUrl && storeId) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${customStylesBucketUrl}/${storeId}.css`;
  document.head.appendChild(link);
}

// Start dataLayer interception early to catch GA4 purchase events
// that may fire before React mounts
purchaseDetector.interceptDataLayerPush();

const createReactRoot = (el: HTMLElement) => createRoot(el);

export function isLoadedInPlayground(): boolean {
  return new URLSearchParams(window.location.search).has('superbarHide');
}

const initCaddie = () => {
  if (isLoadedInPlayground()) {
    return () => {};
  }

  // Shadow DOM is only used in production (when loaded via caddie-ui.js script tag).
  // In dev mode, Vite injects CSS as <style> tags into <head> which don't pierce
  // shadow boundaries, so we fall back to the classic approach.
  const isProductionEmbed = !!caddieScript?.src.includes('caddie-ui.js');

  if (isProductionEmbed) {
    return initWithShadowRoot(caddieScript!);
  }

  return initClassic();
};

/**
 * Production path: mount inside a shadow root for full CSS isolation.
 */
function initWithShadowRoot(script: HTMLScriptElement) {
  const shadowRoot = initShadowRoot(cdnUrl, storeId);
  const mountPoint = shadowRoot.getElementById('caddie-root')!;

  const root = createReactRoot(mountPoint);
  root.render(
    <StrictMode>
      <Lib rootElement={mountPoint} />
    </StrictMode>
  );

  return () => {
    root.unmount();
    cleanupShadowRoot();
  };
}

/**
 * Dev / fallback path: classic mount directly in document.body.
 * Vite injects CSS into <head> which reaches the widget normally.
 */
function initClassic() {
  const caddieRoot = document.createElement('div');
  caddieRoot.id = 'caddie-root';
  caddieRoot.style.pointerEvents = 'none';
  document.body.appendChild(caddieRoot);

  const root = createReactRoot(caddieRoot);
  root.render(
    <StrictMode>
      <Lib rootElement={caddieRoot} />
    </StrictMode>
  );

  return () => {
    root.unmount();
    document.body.removeChild(caddieRoot);
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCaddie);
} else {
  initCaddie();
}
